import type { ReactNode } from 'react';
import { CloudOff, AlertTriangle } from 'lucide-react';
import { Spinner } from '@/shared/components/ui/Spinner';
import { Button } from '@/shared/components/ui/Button';
import {
  GENERATIVE_ERROR_BODY,
  GENERATIVE_ERROR_TITLE,
  GENERATIVE_UNAVAILABLE_BODY,
  GENERATIVE_UNAVAILABLE_TITLE,
} from '../constants';
import type { AICapabilityState } from '../types';

interface CapabilityGateProps {
  state: AICapabilityState;
  children: ReactNode;
  onRetry?: () => void;
  retrying?: boolean;
}

/**
 * Renders generative (Layer 2) content only when the provider is available.
 * Otherwise shows an honest system-state panel — never fabricated AI output.
 */
export function CapabilityGate({ state, children, onRetry, retrying }: CapabilityGateProps) {
  if (state === 'loading') {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner />
      </div>
    );
  }

  if (state === 'available') return <>{children}</>;

  const isError = state === 'error';
  const Icon = isError ? AlertTriangle : CloudOff;

  return (
    <div className="flex flex-col items-center justify-center gap-3 rounded-card border border-dashed border-outline bg-surface-container-low px-6 py-12 text-center">
      <span className="flex h-12 w-12 items-center justify-center rounded-full bg-surface-variant text-on-surface-variant">
        <Icon className="h-6 w-6" />
      </span>
      <h3 className="text-data-lg font-semibold text-on-surface">
        {isError ? GENERATIVE_ERROR_TITLE : GENERATIVE_UNAVAILABLE_TITLE}
      </h3>
      <p className="max-w-md text-body-sm text-on-surface-variant">
        {isError ? GENERATIVE_ERROR_BODY : GENERATIVE_UNAVAILABLE_BODY}
      </p>
      {onRetry && (
        <Button variant="outline" size="sm" loading={retrying} onClick={onRetry}>
          Retry
        </Button>
      )}
    </div>
  );
}
